import { Button, Input, Space, TableProps, Tag, Modal, Tooltip, Flex, App } from "antd";
import { FC, useEffect, useRef, useState } from "react";
import { CommonList, CommonListParams, CommonStateEnum } from "@/types/common";
import { User, UserPermissionEnum } from "types/user";
import WMSAvatar from "styles/WMSAvatar";
import useUserStatus from "./hooks/useUserStatus";
import { AddUser } from "./components/AddUser";
import { UserDetailEdit } from "@/components/UserEdit";
import { getUserAvatarTip } from "@/utils/user";
import { useSelector } from "react-redux";
import { RootState } from "@/store/index"
import { useTranslation } from "react-i18next";
import STableLayout, { DEFAULT_PAGE_SIZE, TableLayoutRef } from "@/styles/STableLayout";
import { ColumnsType } from "antd/lib/table";
import { AnyObject } from "antd/lib/_util/type";
import { userList } from "@/apis/user";
import useDataList from "@/hooks/useDataList";
import SText from "@/styles/SText";
import Permission from "@/components/Permission";
import { getTreeData } from "./util";
import ListFilterBar from "@/components/ListFilterBar";
import useCommittedFilters from "@/components/ListFilterBar/hooks/useCommittedFilters";

interface UserManagerProps {
  status: CommonStateEnum;
}

interface UserFilters {
  keyword: string;
}

const INITIAL_USER_FILTERS: UserFilters = {
  keyword: "",
};

const UserManage: FC<UserManagerProps> = ({ status }) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const currentUser: User = useSelector((state: RootState) => state.userReducer.curUser)
  const [statusModal, setStatusModal] = useState<User | undefined>(undefined);
  const [openStatus, setOpenStatus] = useState<boolean>(false);
  const [detailStatus, setDetailStatus] = useState<User | undefined>(undefined);
  const {
    draftFilters,
    appliedFilters,
    updateDraftFilters,
    commitFilters,
    replaceFilters,
  } = useCommittedFilters<UserFilters>(INITIAL_USER_FILTERS);
  const { keyword } = draftFilters;

  const [list, total, listLoading, { fetchList, addItem, updateItem, rmItem }] = useDataList<User, [CommonList, CommonStateEnum, CommonListParams<string>]>({ fetchApi: userList });

  const [statusLoading, changeStatus] = useUserStatus(status === CommonStateEnum.ACTIVE ? 'disabled' : 'enabled')

  const tableRef = useRef<TableLayoutRef | null>(null);

  const fetch = (offset: number, filters: UserFilters) => {
    const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE
    fetchList("fetchs", status, {
      body: filters.keyword,
      pagination: { offset, take },
    }).catch((err) => {
      switch (err) {
        case 10010:
          break;
        default:
          break;
      }
    });
  };

  useEffect(() => {
    const nextFilters = replaceFilters(INITIAL_USER_FILTERS);
    tableRef.current?.resetTablePage();
    fetch(0, nextFilters);
    // 切换启用/禁用成员时统一重置筛选与页码；fetch 依赖当前 status。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, replaceFilters]);

  const columns: TableProps<User>["columns"] = [
    {
      width: 180,
      title: t('member.avatar'),
      dataIndex: "avatar",
      key: "avatar",
      // render: (_, record) => WMSAvatar({ uniChar: record.id, avatarId: record.avatar, creator: record, alt: getUserAvatarTip(record), size: 22 })
      render: (_, record) => <WMSAvatar uniChar={record.id} avatarId={record.avatar} creator={record} alt={getUserAvatarTip(record)} size={22} />
    },
    {
      title: "Name1",
      dataIndex: "name1",
      key: "name1",
      width: 180,
      render: (name1: string) => <SText text={name1} />
    },
    {
      width: 180,
      title: "Name2",
      dataIndex: "name2",
      key: "name2",
      render: (name2: string) => <SText text={name2} />
    },
    {
      title: t('member.phNumber'),
      dataIndex: "mobile",
      key: "mobile",
      width: 160,
      render: (_, { areaCode, mobile, id }) => (
        <Tag key={id} color="volcano">{`${areaCode} ${mobile}`}</Tag>
      ),
    },
    {
      title: t('common.opt'),
      key: "action",
      width: 240,
      render: (_, user) => (
        <Space size="middle">
          <Button type="link" onClick={() => setDetailStatus(user)}>{t('member.viewDetails')}</Button>
          <Permission
            treeData={getTreeData(t)}
            title={t('org.setPermissions')}
            user={user}
            orgId={null}
            disabled={user.id === currentUser?.id}
            key={user.id}
            defaultPermission={[UserPermissionEnum.ORG_PREVIEW]}
          />
          {status === CommonStateEnum.ACTIVE ? (
            <Button type="link" onClick={() => setStatusModal(user)} disabled={user.id === currentUser.id}>{t('org.disable')}</Button>
          ) : (
            <Button type="link" onClick={() => setStatusModal(user)}>{t('org.enable')}</Button>
          )}
        </Space>
      ),
    },
  ];

  const changeStatusAction = () => {
    changeStatus(statusModal as User)
      .then(() => {
        rmItem(statusModal?.id ?? '')
        setStatusModal(undefined)
        notification.success({ placement: 'top', message: t('member.changeStatusSucc') })
      })
  }

  const handleSearch = () => {
    const nextFilters = commitFilters();
    tableRef.current?.resetTablePage();
    fetch(0, nextFilters);
  };

  const handleClearFilters = () => {
    const nextFilters = replaceFilters(INITIAL_USER_FILTERS);
    tableRef.current?.resetTablePage();
    fetch(0, nextFilters);
  };

  const headerChildren = (
    <Flex justify='space-between' style={{ width: '100%' }}>
      {status === CommonStateEnum.ACTIVE && <Button type="primary" onClick={() => setOpenStatus(true)}>{t('member.addMember')}</Button>}
      <div style={{ flex: 1, minWidth: 0 }}>
        <ListFilterBar
          onSearch={handleSearch}
          onReset={handleClearFilters}
          disabled={listLoading}
        >
          {({ submit }) => (
            <Tooltip title={t('member.searchPrompt')}>
              <Input
                className="input--wrap-search"
                placeholder={t('member.searchPrompt')}
                value={keyword}
                onChange={event => updateDraftFilters({ keyword: event.target.value })}
                onPressEnter={submit}
                style={{ width: 200 }}
              />
            </Tooltip>
          )}
        </ListFilterBar>
      </div>
    </Flex>
  )

  return (
    <Flex vertical style={{ width: '100%', height: '100%' }}>
      <STableLayout
        loading={listLoading}
        dataSource={list}
        columns={columns as ColumnsType<AnyObject>}
        headerChildren={headerChildren}
        tableLayoutRef={tableRef}
        total={total}
        handleChange={offset => fetch(offset, appliedFilters)}
      />
      <Modal title={t('member.tips')} open={!!statusModal} confirmLoading={statusLoading} onOk={changeStatusAction} onCancel={() => setStatusModal(undefined)}>
        <p>{t('member.confirmAction')} {status === CommonStateEnum.ACTIVE ? t('member.disable').toLowerCase() : t('member.enable').toLowerCase()} {t('member.thisMember')}?</p>
        <p>Name1: {statusModal?.name1 ?? ''}</p>
        <p>Name2: {statusModal?.name2 ?? ''}</p>
        <p>{t('member.phNumber')}: {statusModal?.areaCode} {statusModal?.mobile}</p>
      </Modal>
      <AddUser openStatus={openStatus} onOk={addItem} onCancel={() => setOpenStatus(false)} />
      {detailStatus && <UserDetailEdit onCancel={(user) => { user && updateItem(user); setDetailStatus(undefined) }} openStatus={!!detailStatus} userInfo={detailStatus} />}
    </Flex>
  );
};

export default UserManage;
